import { useEffect, useMemo, useState } from "react";
import { Users, HeartPulse, Trophy } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { getNpcRoster } from "@/lib/api";

// Farbliche Klassifikation des Beziehungsstatus (0-100)
function statusFor(score) {
  if (score >= 80) return { label: "Vertrauensvoll", bar: "bg-hammerl-blue", chip: "bg-hammerl-blue text-hammerl-paper" };
  if (score >= 60) return { label: "Gutmütig", bar: "bg-hammerl-highlight", chip: "bg-hammerl-highlight text-hammerl-paper" };
  if (score >= 40) return { label: "Neutral", bar: "bg-hammerl-corpblue", chip: "bg-hammerl-corpblue text-white" };
  if (score >= 20) return { label: "Skeptisch", bar: "bg-hammerl-mixed", chip: "bg-hammerl-mixed text-hammerl-paper" };
  return { label: "Ablehnend", bar: "bg-hammerl-fail", chip: "bg-hammerl-fail text-white" };
}

function initials(name) {
  return name
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export default function NpcRoster({ relationships, completedArcs = [] }) {
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [imgErr, setImgErr] = useState({});

  useEffect(() => {
    let alive = true;
    getNpcRoster()
      .then((d) => {
        if (alive) setRoster(d.npcs || []);
      })
      .catch(() => {
        if (alive) setRoster([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const rels = relationships || {};
  const completedArcSet = useMemo(() => new Set(completedArcs || []), [completedArcs]);

  const scored = useMemo(
    () =>
      roster.map((n) => ({
        ...n,
        score: Number.isFinite(rels[n.key]) ? rels[n.key] : rels[n.name] ?? 50,
        // v14.2: Arc-Abschluss über die von der API mitgelieferte arc_id prüfen
        // (robuster als Substring-Heuristik — funktioniert auch für "Sheriff Ümüt")
        arcDone: !!n.arc_id && completedArcSet.has(n.arc_id),
      })),
    [roster, rels, completedArcSet]
  );

  if (loading) {
    return (
      <div className="bg-hammerl-card border-2 border-hammerl-ink p-4">
        <div className="font-mono text-[10px] uppercase tracking-widest text-hammerl-pencil">Beziehungen · lade…</div>
      </div>
    );
  }

  if (scored.length === 0) return null;

  return (
    <div className="bg-hammerl-card border-2 border-hammerl-ink p-4" data-testid="npc-roster">
      <div className="flex items-center gap-2 mb-3">
        <Users className="w-4 h-4 text-hammerl-corpblue" />
        <div className="font-mono text-[10px] uppercase tracking-widest text-hammerl-pencil">Beziehungen</div>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 gap-2">
        {scored.map((n) => {
          const st = statusFor(n.score);
          const showFallback = imgErr[n.key];
          return (
            <button
              key={n.key}
              type="button"
              onClick={() => setSelected(n)}
              data-testid={`npc-roster-${n.key.toLowerCase().replace(/\s+/g, "-")}`}
              className="group flex flex-col items-center gap-1 p-1.5 border-2 border-hammerl-ink bg-hammerl-paper hover:bg-hammerl-sidebar hover:-translate-y-[1px] hover:shadow-brutal-sm transition-all focus:outline-none focus:ring-2 focus:ring-hammerl-corpblue"
              title={`${n.name} · ${n.score}/100 · ${st.label}`}
            >
              <div className="relative w-12 h-12 border-2 border-hammerl-ink overflow-hidden bg-hammerl-sidebar shrink-0">
                {!showFallback ? (
                  <img
                    src={n.image}
                    alt={n.name}
                    className="w-full h-full object-cover"
                    onError={() => setImgErr((prev) => ({ ...prev, [n.key]: true }))}
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-hammerl-corpblue text-white font-bold text-sm">
                    {initials(n.name)}
                  </div>
                )}
                {n.arcDone && (
                  <div
                    className="absolute -top-1 -right-1 w-5 h-5 bg-hammerl-highlight border-2 border-hammerl-ink flex items-center justify-center shadow-brutal-sm"
                    data-testid={`npc-arc-trophy-${n.key.toLowerCase().replace(/\s+/g, "-")}`}
                    title="NPC-Neben-Arc abgeschlossen"
                  >
                    <Trophy className="w-2.5 h-2.5 text-hammerl-ink" />
                  </div>
                )}
              </div>
              <div className="font-mono text-[9px] font-bold text-hammerl-ink text-center leading-tight line-clamp-2 min-h-[22px]">
                {n.name.split(" ").slice(-1)[0]}
              </div>
              <div className="w-full h-1 bg-hammerl-sidebar border border-hammerl-ink overflow-hidden">
                <div className={`h-full ${st.bar}`} style={{ width: `${Math.max(2, Math.min(100, n.score))}%` }} />
              </div>
              <div className="font-mono text-[9px] text-hammerl-pencil">{n.score}/100</div>
            </button>
          );
        })}
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => { if (!v) setSelected(null); }}>
        <DialogContent
          className="max-w-md rounded-none border-2 border-hammerl-ink shadow-brutal bg-hammerl-card p-0 overflow-hidden"
          data-testid="npc-detail-dialog"
        >
          {selected && (() => {
            const st = statusFor(selected.score);
            const showFallback = imgErr[selected.key];
            return (
              <>
                <div className="relative w-full aspect-square bg-hammerl-sidebar border-b-2 border-hammerl-ink">
                  {!showFallback ? (
                    <img
                      src={selected.image}
                      alt={selected.name}
                      className="w-full h-full object-cover"
                      onError={() => setImgErr((prev) => ({ ...prev, [selected.key]: true }))}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-hammerl-corpblue text-white font-bold text-6xl">
                      {initials(selected.name)}
                    </div>
                  )}
                </div>
                <div className="px-5 py-4 space-y-3">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-hammerl-pencil">{selected.role}</div>
                    <DialogTitle className="font-bold text-xl leading-tight" data-testid="npc-detail-name">
                      {selected.name}
                    </DialogTitle>
                  </div>
                  <DialogDescription className="text-sm text-hammerl-ink leading-relaxed" data-testid="npc-detail-description">
                    {selected.description}
                  </DialogDescription>
                  <div className="pt-2 border-t-2 border-dashed border-hammerl-pencil">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-hammerl-pencil">
                        <HeartPulse className="w-3 h-3" /> Beziehungsstatus
                      </div>
                      <span className={`inline-flex items-center px-2 py-0.5 border-2 border-hammerl-ink font-mono text-[10px] font-bold ${st.chip}`}>
                        {st.label}
                      </span>
                    </div>
                    <div className="w-full h-2 bg-hammerl-sidebar border-2 border-hammerl-ink overflow-hidden">
                      <div className={`h-full ${st.bar}`} style={{ width: `${Math.max(2, Math.min(100, selected.score))}%` }} />
                    </div>
                    <div className="font-mono text-[10px] text-hammerl-pencil mt-1 text-right">{selected.score} / 100</div>
                  </div>
                  {selected.arcDone && (
                    <div
                      className="mt-1 flex items-center gap-2 px-3 py-2 border-2 border-hammerl-ink bg-hammerl-highlight text-hammerl-paper shadow-brutal-sm"
                      data-testid="npc-detail-arc-trophy"
                    >
                      <Trophy className="w-4 h-4 shrink-0" />
                      <div className="font-mono text-[10px] uppercase tracking-widest">
                        Persönlichen NPC-Arc abgeschlossen
                      </div>
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
